import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../services/AuthService';
import { Router } from '@angular/router';
import { NotificationService } from '../services/NotificationService';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthPayload } from '../auth/auth.types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { apiErrorText } from '../shared/api-error-text';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {
  username = '';
  password = '';
  rememberMe = false;
  isCheckingSession = true;
  isNavigating = false;

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private notificationService: NotificationService,
    private spinner: NgxSpinnerService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.checkExistingSession();
  }

  private checkExistingSession(): void {
    // К моменту инициализации этого компонента APP_INITIALIZER уже отработал:
    // если /me вернул 200 — sessionStorage заполнен; если 401/403 — сессия очищена.
    if (this.authService.isLoggedIn() && this.hasAdminAccess()) {
      this.navigateAdmin();
      return;
    }
    if (this.authService.isLoggedIn() && this.authService.hasRole('client')) {
      this.navigateClient();
      return;
    }
    this.isCheckingSession = false;
  }

  private hasAdminAccess(): boolean {
    return this.authService.isSuperAdmin() || this.authService.getPermissions().length > 0;
  }

  private navigateAdmin(): void {
    if (this.isNavigating) return;
    this.isNavigating = true;
    this.router.navigate(['/admin']).finally(() => this.isNavigating = false);
  }

  private navigateClient(): void {
    if (this.isNavigating) return;
    this.isNavigating = true;
    this.router.navigate(['/client']).finally(() => this.isNavigating = false);
  }

  private navigateBasedOnPayload(payload: AuthPayload): void {
    if (payload.is_super_admin || (payload.permissions?.length ?? 0) > 0) {
      this.navigateAdmin();
    } else if (payload.roles?.includes('client')) {
      this.navigateClient();
    } else {
      this.notificationService.showNotification('У аккаунта нет ролей. Обратитесь к администратору.');
    }
  }

  login(): void {
    if (this.isCheckingSession) return;
    if (!this.username || !this.password) {
      this.notificationService.showNotification('Заполните все поля!');
      this.clearAfterRequest();
      return;
    }
    this.spinner.show();
    this.authService.login(this.username, this.password)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.spinner.hide();
          this.authService.setSession(response, this.rememberMe);
          this.navigateBasedOnPayload(response);
        },
        error: (error) => {
          this.spinner.hide();
          this.handleLoginError(error);
        }
      });
  }

  private handleLoginError(error: any): void {
    const messages: Record<401 | 403 | 422, string> = {
      401: 'Неверный логин или пароль.',
      403: 'Доступ запрещен.',
      422: 'Ошибка ввода данных.'
    };
    const message = apiErrorText(error, messages[error.status as keyof typeof messages] || 'Произошла ошибка сервера.');
    this.notificationService.showNotification(message);
  }

  clearAfterRequest(): void {
    this.username = '';
    this.password = '';
    this.rememberMe = false;
  }
}
